// Which entries on one day claim the same stretch of time twice.
//
// Two shapes are legitimate and must not be flagged:
//  - entries that merely touch (one ends as the next starts);
//  - payroll-split twins: the weekly-overtime splitter copies a session's exact
//    clock-in and clock-out onto a second row and moves the excess hours to it,
//    so the pair shares one range while their hours add up to that session.
//
// The server's overlap trigger lets ANY identical pair through on that basis,
// so a person typing one entry's times onto another produced the same shape
// with both rows claiming the full day (2026-09-22: 8h Base + 8h OT 1.5x for
// one 07:30–16:00). The tell is the hours: twins share the span, a copy
// doubles it. A group of identical ranges is flagged when its hours exceed the
// span itself.

export interface OverlapCandidate {
  id: number;
  clockInTime: string;
  clockOutTime: string | null;
  totalHours: number;
}

// Rounding slack: total_hours is stored to 2dp.
const HOURS_EPSILON = 0.02;

export function findOverlappingEntries(entries: OverlapCandidate[]): Set<number> {
  const closed = entries
    .filter(e => e.clockOutTime)
    .map(e => ({
      id: e.id,
      start: Date.parse(e.clockInTime),
      end: Date.parse(e.clockOutTime as string),
      hours: Number(e.totalHours) || 0,
    }))
    .filter(e => Number.isFinite(e.start) && Number.isFinite(e.end) && e.start < e.end);

  const flagged = new Set<number>();

  // Identical ranges: judge the group by its hours.
  const groups = new Map<string, typeof closed>();
  for (const e of closed) {
    const key = `${e.start}-${e.end}`;
    const group = groups.get(key) ?? [];
    group.push(e);
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const spanHours = (group[0].end - group[0].start) / 3_600_000;
    const claimed = group.reduce((sum, e) => sum + e.hours, 0);
    if (claimed > spanHours + HOURS_EPSILON) group.forEach(e => flagged.add(e.id));
  }

  // Different ranges that cross: always a clash.
  for (let i = 0; i < closed.length; i++) {
    for (let j = i + 1; j < closed.length; j++) {
      const a = closed[i];
      const b = closed[j];
      const identical = a.start === b.start && a.end === b.end;
      if (!identical && a.start < b.end && b.start < a.end) {
        flagged.add(a.id);
        flagged.add(b.id);
      }
    }
  }

  return flagged;
}
